import {
  ContextMenu,
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
  Tree,
  TreeNodeInfo
} from "@blueprintjs/core";
import { assertNever } from "assert-never";
import * as Array from "fp-ts/Array";
import * as Eq from "fp-ts/Eq";
import * as Set from "fp-ts/Set";
import React from "react";

import { DEMO_MODE, DEMO_MODE_DISABLED_TEXT } from "../DemoMode";
import { Panic } from "../Errors/Panic";
import { Entry, FileTreeView } from "../gen/FileTreeView";
import { HexgripError } from "../gen/HexgripError";

export interface FileTreeViewPanelProps {
  fileTreeView: FileTreeView;
  onFileDoubleClick: (path: string[]) => void;
}

function FileTreeViewPanel(props: FileTreeViewPanelProps): JSX.Element {
  switch (props.fileTreeView.kind) {
    case "error":
      return <FileTreeViewError error={props.fileTreeView.value} />;
    case "entries":
      return (
        <FileTreeViewFiles
          entries={props.fileTreeView.value}
          onFileDoubleClick={props.onFileDoubleClick}
        />
      );
    default:
      return assertNever(props.fileTreeView);
  }
}

interface FileTreeViewFilesPanelProps {
  entries: Entry[];
  onFileDoubleClick: (path: string[]) => void;
}

interface NodeData {
  path: string[];
  isFile: boolean;
}

const E = Array.getEq(Eq.eqString);

function FileTreeViewFiles(props: FileTreeViewFilesPanelProps): JSX.Element {
  const [expanded, setExpanded] = React.useState<Set<string[]>>(Set.empty);
  const [contextMenuTreeNode, setContextMenuTreeNode] = React.useState<string[] | null>(null);

  function convert(path: string[], entry: Entry): TreeNodeInfo<NodeData> {
    switch (entry.kind) {
      case "file": {
        const entryPath = path.concat([entry.value.name]);
        return {
          id: entry.value.name,
          label: entry.value.name,
          icon: "document",
          nodeData: {
            path: entryPath,
            isFile: true
          },
          secondaryLabel: <Icon icon="eye-open" />,
          isSelected:
            contextMenuTreeNode === null ? false : E.equals(contextMenuTreeNode, entryPath)
        };
      }
      case "directory": {
        const entryPath = path.concat([entry.value.name]);
        const isExpanded = Set.elem(E)(entryPath)(expanded);
        return {
          id: entry.value.name,
          label: entry.value.name,
          icon: isExpanded ? "folder-open" : "folder-close",
          hasCaret: false,
          childNodes: entry.value.children.map((x) => convert(entryPath, x)),
          isExpanded: isExpanded,
          nodeData: {
            path: entryPath,
            isFile: false
          },
          isSelected:
            contextMenuTreeNode === null ? false : E.equals(contextMenuTreeNode, entryPath)
        };
      }
      case "failedDirectory":
        throw new Error("TODO");
      default:
        return assertNever(entry);
    }
  }

  return (
    <Tree
      contents={props.entries.map((x) => convert([], x))}
      onNodeClick={(treeNode, path) => {
        const nodeData = treeNode.nodeData;
        if (nodeData === undefined) {
          throw new Panic("Missing nodeData", { path });
        }
        const exists = Set.elem(E)(nodeData.path)(expanded);
        if (exists) {
          setExpanded(Set.remove(E)(nodeData.path)(expanded));
        } else {
          setExpanded(Set.insert(E)(nodeData.path)(expanded));
        }
      }}
      onNodeDoubleClick={(treeNode, path) => {
        const nodeData = treeNode.nodeData;
        if (nodeData === undefined) {
          throw new Panic("Missing nodeData", { path });
        }
        if (nodeData.isFile) {
          props.onFileDoubleClick(nodeData.path);
        }
      }}
      onNodeContextMenu={(treeNode, path, e) => {
        e.preventDefault();

        const nodeData = treeNode.nodeData;
        if (nodeData === undefined) {
          throw new Panic("Missing nodeData", { path });
        }
        setContextMenuTreeNode(nodeData.path);
        ContextMenu.show(
          <Menu>
            <MenuDivider title={JSON.stringify(path)} />
            <MenuDivider />
            <MenuItem
              icon="document"
              text="New File"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            />
            <MenuItem
              icon="folder-new"
              text="New Folder"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
              onClick={() => {}}
            />
            <MenuItem
              icon="flows"
              text="Rename"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            />
            <MenuItem
              icon="trash"
              text="Delete"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            />
            <MenuDivider />
            <MenuItem
              icon="cloud-download"
              text="Download to PC"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            />
          </Menu>,
          {
            left: e.clientX,
            top: e.clientY
          },
          () => {
            setContextMenuTreeNode(null);
          }
        );
      }}
    />
  );
}

interface FileTreeViewErrorProps {
  error: HexgripError;
}

function FileTreeViewError(props: FileTreeViewErrorProps): JSX.Element {
  return (
    <>
      <div>Error</div>
      <pre>{props.error.error}</pre>
    </>
  );
}

export default FileTreeViewPanel;
